import { useState } from 'react';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { AlertCircle, Link2, Link2Off, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { logger } from '@/features/help/utils/logger';

/**
 * Formats CRM provider name for display
 */
const formatCrmProviderName = (provider: string | null | undefined): string => {
  if (!provider) return 'Not Connected';
  const names: Record<string, string> = {
    salesforce: 'Salesforce',
    hubspot: 'HubSpot',
    zoho: 'Zoho CRM',
    gohighlevel: 'GoHighLevel',
  };
  return names[provider] || provider;
};

/**
 * Component for managing CRM connection settings.
 * Allows users to view their current CRM connection status and disconnect if needed.
 */
export const CrmConnectionSection = () => {
  const [isCrmDisconnecting, setIsCrmDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const { appUser, disconnectCrm, isCrmConnected } = useAuthStore();

  /**
   * Handles CRM disconnection after user confirmation.
   * Uses window.location.href for a full page reload to ensure the dashboard loader
   * re-runs and detects the disconnected CRM state, triggering the OAuth flow.
   */
  const handleDisconnectCrm = async () => {
    logger.info('CrmConnectionSection', 'Executing CRM disconnect after confirmation');
    setShowDisconnectConfirm(false);
    setIsCrmDisconnecting(true);

    const success = await disconnectCrm();
    setIsCrmDisconnecting(false);

    if (success) {
      toast.success('CRM disconnected successfully. Redirecting to reconnect...');
      logger.info('CrmConnectionSection', 'CRM disconnected, triggering full page reload to re-run dashboard loader');
      // Use window.location.href for a full page reload instead of React Router's navigate()
      // This ensures the dashboard loader re-runs and detects the disconnected CRM state
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1500);
    } else {
      toast.error('Failed to disconnect CRM. Please try again.');
      logger.error('CrmConnectionSection', 'CRM disconnect failed');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">CRM Connection</h2>

      <div className="space-y-4">
        {/* Connection Status */}
        <div className="bg-white p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isCrmConnected ? (
                <Link2 className="h-5 w-5 text-green-600" />
              ) : (
                <Link2Off className="h-5 w-5 text-red-600" />
              )}
              <div>
                <p className="font-medium text-gray-900">
                  {formatCrmProviderName(appUser?.crmProvider)}
                </p>
                <p className="text-sm text-gray-500">
                  {isCrmConnected ? 'Connected and active' : 'Not connected'}
                </p>
              </div>
            </div>
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                isCrmConnected
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {isCrmConnected ? 'Active' : 'Disconnected'}
            </span>
          </div>
        </div>

        {/* Disconnect Confirmation */}
        {showDisconnectConfirm && (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <div className="flex gap-2 items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-yellow-800">Disconnect CRM?</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    This will remove your {formatCrmProviderName(appUser?.crmProvider)} connection.
                    You'll need to reconnect to continue using the application. Your field mappings will be deleted and you will need to create new ones when you reconnect.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleDisconnectCrm}
                    disabled={isCrmDisconnecting}
                    className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                  >
                    {isCrmDisconnecting && <RefreshCw className="h-3 w-3 animate-spin" />}
                    {isCrmDisconnecting ? 'Disconnecting...' : 'Yes, Disconnect'}
                  </button>
                  <button
                    onClick={() => setShowDisconnectConfirm(false)}
                    disabled={isCrmDisconnecting}
                    className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {isCrmConnected && !showDisconnectConfirm && (
          <div className="flex gap-3">
            <button
              onClick={() => {
                logger.debug('CrmConnectionSection', 'Disconnect CRM button clicked, showing confirmation');
                setShowDisconnectConfirm(true);
              }}
              disabled={isCrmDisconnecting}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Link2Off className="h-4 w-4" />
              Disconnect CRM
            </button>
          </div>
        )}

        {/* Info Note */}
        <p className="text-xs text-gray-500">
          Disconnecting your CRM will require you to re-authenticate. This is useful if you need to
          switch accounts or refresh your connection permissions.
        </p>
      </div>
    </div>
  );
};

